import {
  Installation_energy,
  No_of_packs,
  All_time_energythroughput,
  MAN_part_no,
  Software_stage,
  DOD_config,
  SOC,
  Depth_of_discharge,
  Battery_temp,
  Calendric_aging,
  // Efficiency,
  A_cal_val,
  Cyclic_aging,
  A_cyc_val,
  SOH
} from './battery_analysis_kpi';

import BatteryDataDownload from './battery_analysis_kpi/BatteryDataDownload'; // 🔋 Added battery data download component

import { useGraphContext } from '../GraphContext';
import { Card } from "react-bootstrap";
import CommonKPI from './battery_analysis_kpi/CommonKPI';

const BatteryAnalysis = () => {
  const { graphData } = useGraphContext();

  const {
    installed_energy,
    no_of_packs,
    All_time_energy_throughput,
    man_part_no,
    software_stage,
    dod_Config,
    soc,
    startDate,
    endDate,
    showECU,
    depth_of_discharge,
    vin_short,
    battery_temp,
    efficiency,
    calendric_aging_matrix,
    a_cal_val,
    cyc_aging_matrix,
    a_cyc_val,
    soh,
    batterytemp_avg_statement,
  } = graphData;

  const KPIData = [
    {
      title: 'Calendaric SoH', value: graphData.cal_soh_bam, width: '6', height: '85%', description: `<p><strong>Definition</strong></p>

<p>
  The <strong>calendaric state of health (SoH)</strong> describes the portion
  of battery capacity loss caused by time-dependent aging, independent of
  vehicle usage. It reflects degradation effects related to calendar time,
  ambient temperature, and storage conditions.
</p>

<p><strong>Source</strong></p>

<p>
  Calendaric <strong>SoH</strong> from MAN Battery Aging Model.
</p>

<p><strong>Time series data</strong></p>

<ul>
  <li>
    <strong>Single vehicle:</strong>
    Calendaric <strong>SoH</strong> is visualized over time since the start of
    data acquisition.
  </li>

  <li>
    <strong>Multiple vehicles:</strong>
    Calendaric <strong>SoH</strong> over time is shown using the 10th percentile,
    median, and 90th percentile. The y-axis represents <strong>SoH</strong> (%),
    and the x-axis represents time (days since start of data acquisition).
    At longer time spans, curves may rise slightly because only a few vehicles
    provide data at these durations. This behavior is expected and not a
    visualization error.
  </li>
</ul> ` },
    {
      title: 'Cyclic SoH', value: graphData.cyc_soh_bam, width: '6', height: '85%', description: ` <p><strong>Definition</strong></p>

<p>
  The <strong>cyclic state of health (SoH)</strong> describes the portion of
  battery capacity loss caused by usage-related aging. It reflects degradation
  due to charging and discharging cycles and energy throughput.
</p>

<p><strong>Source</strong></p>

<p>
  Cyclic <strong>SoH</strong> from MAN Battery Aging Model.
</p>

<p><strong>Time series data</strong></p>

<ul>
  <li>
    <strong>Single vehicle:</strong>
    Cyclic <strong>SoH</strong> is visualized over time since the start of
    data acquisition.
  </li>

  <li>
    <strong>Multiple vehicles:</strong>
    Cyclic <strong>SoH</strong> over time is shown using the 10th percentile,
    median, and 90th percentile. The y-axis represents <strong>SoH</strong> (%),
    and the x-axis represents time (days since start of data acquisition).
    At longer time spans, curves may rise slightly because only a small number
    of vehicles have data available. This is expected behavior and not a bug.
  </li>
</ul>` },
    {
      title: 'Cyclic aging factor', width: '6', height: '88%',

      // description: `<p><strong>Definition</strong></p>

      // <p>
      //   This KPI is a single aggregated indicator that quantifies the overall severity
      //   of <strong>cyclic aging</strong> (usage-related battery degradation from
      //   charge/discharge cycles) for a vehicle. It reflects how much capacity loss
      //   is attributed to the vehicle’s charging and discharging behavior since the
      //   start of data acquisition.
      // </p>

      // <p><strong>Source</strong></p>

      // <p>
      //   Calculated using the MAN Battery Aging Model.
      // </p>

      // <p><strong>Unit</strong></p>

      // <p>
      //   %-<strong>SoH</strong> loss / (days^b), where <strong>b</strong> indicates how the aging
      //   rate decelerates over time (a degressive aging coefficient).
      // </p>

      // <p><strong>Visualization</strong></p>

      // <ul>
      //   <li>
      //     <strong>Single vehicle:</strong>
      //     Displayed as an individual cyclic aging factor value for the selected
      //     vehicle (one number indicating its cyclic aging severity).
      //   </li>

      //   <li>
      //     <strong>Multiple vehicles:</strong>
      //     Displayed as a histogram of cyclic aging factor values across all selected
      //     vehicles. Each bar (bin) in the chart represents a range of factor values,
      //     and its height indicates the number of vehicles whose cyclic aging factor
      //     falls within that range.
      //   </li>
      // </ul>`
    },
    {
      title: 'Calendaric aging factor', value: graphData.a_cal_avg, width: '6', height: '88%', description: `<p><strong>Definition</strong></p>

<p>
  This KPI is a single aggregated indicator that quantifies the overall severity
  of <strong>calendaric aging</strong> (time-based battery degradation) for a vehicle.
  It represents how much capacity loss is attributed to calendar time under the
  vehicle’s operating conditions (state-of-charge and ambient temperature)
  since the start of data acquisition.
</p>

<p><strong>Source</strong></p>

<p>
  Calculated using the MAN Battery Aging Model.
</p>

<p><strong>Unit</strong></p>

<p>
  %-<strong>SoH</strong> loss / (days^b), where <strong>b</strong> indicates how the aging
  rate decelerates over time (a degressive aging coefficient).
</p>

<p><strong>Visualization</strong></p>

<ul>
  <li>
    <strong>Single vehicle:</strong>
    Displayed as an individual calendaric aging factor value for the selected
    vehicle (one number indicating its calendaric aging severity).
  </li>

  <li>
    <strong>Multiple vehicles:</strong>
    Displayed as a histogram of calendaric aging factor values across all
    selected vehicles. Each bar (bin) in the chart represents a range of factor
    values, and its height indicates the number of vehicles whose calendaric
    aging factor falls within that range.
  </li>
</ul> ` },
    {
      title: 'Cyclic aging matrix', value: graphData.cyc_aging_matrix, width: '12', height: '100vh', description: ` <p><strong>Definition</strong></p>

<p>
  This matrix shows where battery capacity loss due to <strong>cyclic aging</strong>
  (usage-related degradation from charge/discharge cycles) occurred, as a function
  of battery usage patterns since the start of data acquisition.
  It is typically defined by cycle conditions (for example, ranges of start SoC
  (technical) vs. end SoC (technical) for charging/discharging events),
  highlighting how different usage behaviors contributed to <strong>SoH</strong> loss.
</p>

<p><strong>Source</strong></p>

<p>
  Based on MAN Battery Aging Model calculations.
</p>

<p><strong>Unit</strong></p>

<p>
  %-<strong>SoH</strong> loss / (days^b), where <strong>b</strong> indicates how the aging
  rate slows down (degresses) over time.
</p>

<p><strong>Visualization</strong></p>

<ul>
  <li>
    <strong>Single vehicle:</strong>
    Displays the vehicle’s cyclic <strong>SoH</strong> loss distributed across the matrix
    of cycle conditions (showing the impact of various charge/discharge depth
    and technical SoC ranges on aging).
  </li>

  <li>
    <strong>Multiple vehicles:</strong>
    Each matrix cell shows the average cyclic <strong>SoH</strong> loss for the respective
    cycle condition across all selected vehicles.
  </li>
</ul>`
    },
    {
      title: 'Calendaric aging matrix', value: graphData.cal_aging, width: '12', height: '100vh', description: `<p><strong>Definition</strong></p>

<p>
  This matrix shows where battery capacity loss due to <strong>calendaric aging</strong>
  (time-based degradation) occurred, plotted across combinations of battery
  state-of-charge (SoC) and ambient temperature since the start of data acquisition.
  It highlights which SoC–temperature conditions the battery mostly experienced
  and how much <strong>SoH</strong> loss is attributed to each.
</p>

<p><strong>Source</strong></p>

<p>
  Based on MAN Battery Aging Model calculations.
</p>

<p><strong>Unit</strong></p>

<p>
  %-<strong>SoH</strong> loss / (days^b), where <strong>b</strong> indicates how the aging
  rate slows down (degresses) over time.
</p>

<p><strong>Visualization</strong></p>

<ul>
  <li>
    <strong>Single vehicle:</strong>
    Displays the vehicle’s calendaric <strong>SoH</strong> loss distributed across the
    SoC–temperature matrix cells (showing the impact of time spent in each condition).
  </li>

  <li>
    <strong>Multiple vehicles:</strong>
    Each matrix cell shows the <strong>average</strong> calendaric <strong>SoH</strong> loss for that
    SoC-temperature combination across all selected vehicles
    (i.e., an average of the group’s loss in each condition).
  </li>
</ul>`
    }
  ];

  return (
    <div>
      {/* 🔋 Battery Data Download Component */}
      {/* <BatteryDataDownload /> */}
      <BatteryDataDownload />

      <div className="card-info">
        <Installation_energy value={installed_energy} />
        <No_of_packs value={no_of_packs} />
        <All_time_energythroughput value={{ vin_short, All_time_energy_throughput }} />
        <MAN_part_no value={man_part_no} />
        <Software_stage value={software_stage} />
        <DOD_config value={dod_Config} />
      </div>

      <div className="row">
        <div className="col-md-6 mb-3 d-flex" >
          <Card className="flex-fill">
            <SOH value={{ soh, vin_short, showECU }} />
          </Card>
        </div>

        <div className="col-md-6 mb-3 d-flex" >
          <Card className="flex-fill">
            <Depth_of_discharge value={{ depth_of_discharge, vin_short }} />
          </Card>
        </div>

        {/* Created a Common Component to render KPI Data */}
        {KPIData.map((kpi, index) => (
          <div key={index} className={`col-md-${kpi.width} mb-3 d-flex`} >
            <Card className="flex-fill">
              <CommonKPI value={kpi.value} kpiTitle={kpi.title} KpiDescription={kpi.description} KPIheight={kpi.height} />
            </Card>
          </div>
        ))}
        <div className="col-md-6 mb-3 d-flex ">
          <Card className="flex-fill" >
            <Battery_temp value={{ battery_temp, vin_short, batterytemp_avg_statement, startDate, endDate }} />
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BatteryAnalysis;
